import { useState, useContext, forwardRef } from 'react';
import { Button } from 'remote-com-shared/components/Button';
import {
  Navigation,
  NavigationSecondary,
} from 'remote-com-shared/components/HeaderNavigation/styles/Navigation';
import HeroContext from 'remote-com-shared/components/Hero/context';
import { getDocumentMeta } from 'remote-com-shared/domains/navigation/hrefResolver';
import { useBreakpointRange } from 'remote-com-shared/style/breakpoints';
import styled, { createGlobalStyle, css } from 'styled-components';

import { Wrapper, RemoteLogo } from './styles';
import { BrandContainer, Brand } from './styles/Brand';

import {
  fromDesktopBreakpoint,
  fromLargeDesktopBreakpoint,
} from '@/src/domains/shared/breakpoints';

const WrapperPosition = styled.div`
  position: relative;
  font-family: var(--typography-fontBrand);

  && a:focus-visible {
    box-shadow: 0 0 0 3px rgba(20, 25, 30, 0.75);
  }

  ${Wrapper} {
    @media print {
      visibility: hidden;

      ${Brand} {
        visibility: visible;

        svg {
          color: var(--colors-blank);
        }
      }
    }
  }
`;

// Registered so one transition drives both the clip-path notch and its corner strokes (the same
// reason Nova registers --nv-notch-*), keeping the two in sync mid-animation.
const CTAProperties = createGlobalStyle`
  @property --nv-cta-notch {
    syntax: '<length>';
    inherits: true;
    initial-value: 0px;
  }

  @property --nv-cta-press {
    syntax: '<length>';
    inherits: true;
    initial-value: 0px;
  }
`;

// Restyles the shared Button as the Nova button (packages/nova .../button/button.module.scss,
// Outline, size sm, on a white fill): 6px radius, 2px stroke, a notch cut into the top-left and
// bottom-right corners on hover with the stroke redrawn around it, a 2px inset press, and the blue
// focus ring. Like Nova's Outline there is no fill change on hover or press.
// The shared Button reads its colour from the styled-components theme (old iris), not from CSS
// variables, so the rebrand scope in BenefitsLayout cannot reach it and every value is set here.
const HeaderCTA = styled(Button)`
  &&& {
    --button-colorText: #14191e;
    --nv-fill: #ffffff;
    --nv-stroke: #14191e;
    --nv-bw: 2px;
    --nv-notch-size: 8px;
    --nv-cta-notch: 0px;
    --nv-cta-press: 0px;

    position: relative;
    isolation: isolate;
    box-sizing: border-box;
    min-width: 0;
    height: 40px;
    min-height: 40px;
    padding: 8px 16px;
    border: 0;
    border-radius: 6px;
    background: none;
    filter: none;
    font-family: var(--typography-fontBrand);
    font-size: 16px;
    font-weight: 400;
    line-height: 1.5;
    transition: --nv-cta-notch 250ms cubic-bezier(0.4, 0, 1, 1),
      --nv-cta-press 150ms cubic-bezier(0, 0, 0.2, 1);

    /* The painted shape: fill + inset stroke, clipped by the notch (Figma's boolean subtract). */
    &::before {
      content: '';
      position: absolute;
      inset: var(--nv-cta-press);
      z-index: -1;
      border-radius: 6px;
      background-color: var(--nv-fill);
      box-shadow: inset 0 0 0 var(--nv-bw) var(--nv-stroke);
      clip-path: polygon(
        var(--nv-cta-notch) 0,
        100% 0,
        100% calc(100% - var(--nv-cta-notch)),
        calc(100% - var(--nv-cta-notch)) calc(100% - var(--nv-cta-notch)),
        calc(100% - var(--nv-cta-notch)) 100%,
        0 100%,
        0 var(--nv-cta-notch),
        var(--nv-cta-notch) var(--nv-cta-notch)
      );
      transition: background-color 250ms cubic-bezier(0.4, 0, 1, 1);
    }

    /* clip-path also removes the stroke along the two cut edges; these L-shaped corners put it back. */
    &::after {
      content: '';
      position: absolute;
      inset: var(--nv-cta-press);
      z-index: -1;
      pointer-events: none;
      background-repeat: no-repeat;
      background-image: linear-gradient(var(--nv-stroke), var(--nv-stroke)),
        linear-gradient(var(--nv-stroke), var(--nv-stroke)),
        linear-gradient(var(--nv-stroke), var(--nv-stroke)),
        linear-gradient(var(--nv-stroke), var(--nv-stroke));
      background-position: left calc(var(--nv-cta-notch) - var(--nv-bw)) top 0,
        left 0 top calc(var(--nv-cta-notch) - var(--nv-bw)),
        right calc(var(--nv-cta-notch) - var(--nv-bw)) bottom 0,
        right 0 bottom calc(var(--nv-cta-notch) - var(--nv-bw));
      background-size: var(--nv-bw) var(--nv-cta-notch), var(--nv-cta-notch) var(--nv-bw),
        var(--nv-bw) var(--nv-cta-notch), var(--nv-cta-notch) var(--nv-bw);
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        --nv-cta-notch: var(--nv-notch-size);
        transition: --nv-cta-notch 300ms cubic-bezier(0, 0, 0.2, 1),
          --nv-cta-press 150ms cubic-bezier(0, 0, 0.2, 1);
      }
    }

    &:active {
      --nv-cta-notch: 0px;
      --nv-cta-press: 2px;
    }

    &:focus-visible,
    &:focus.focus-visible {
      outline: 2px solid #057aff;
      outline-offset: 2px;
      box-shadow: none;
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }

  @media print {
    display: none;
  }
`;

const CTAMobile = styled.div`
  display: block;

  ${fromLargeDesktopBreakpoint(css`
    display: none;
  `)}
`;

const BenefitsHeader = forwardRef(function BenefitsHeader(props, ref) {
  const { compactDisplayCTA = true, brandHref = '/', brandSuffix, document } = props;
  const { isIntersecting } = useContext(HeroContext);
  const [isOpen, setIsOpen] = useState(false);
  const { type, uid } = getDocumentMeta(document);

  const printUrl = `/print?type=${type}&uid=${uid}`;

  useBreakpointRange(
    { to: fromDesktopBreakpoint },
    {
      onLeave: () => setIsOpen(false),
    }
  );
  return (
    <WrapperPosition>
      <CTAProperties />
      <Wrapper $isOpen={isOpen} $isFixed={!isIntersecting} ref={ref} {...props}>
        <BrandContainer $isOpen={isOpen} $isFixed={!isIntersecting}>
          <Brand as="a" aria-label="Visit homepage" href={brandHref}>
            <RemoteLogo $isOpen={isOpen} $isFixed={!isIntersecting} />
            {brandSuffix && brandSuffix({ $isOpen: isOpen, $isFixed: !isIntersecting })}
          </Brand>

          <CTAMobile>
            {compactDisplayCTA && <HeaderCTA href={printUrl}>Download as PDF</HeaderCTA>}
          </CTAMobile>
        </BrandContainer>

        <Navigation $isOpen={isOpen} aria-hidden={!isOpen}>
          <NavigationSecondary>
            <HeaderCTA href={printUrl}>Download as PDF</HeaderCTA>
          </NavigationSecondary>
        </Navigation>
      </Wrapper>
    </WrapperPosition>
  );
});

export default BenefitsHeader;
